import React, { useState, useEffect } from 'react';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

export default function SmartSelect({
  value,
  onChange,
  onSelect,
  initialRecord,
  model,
  labelField = 'name',
  placeholder = 'Search...',
  creatable = true,
}) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [metadata, setMetadata] = useState([]);
  const [newRecord, setNewRecord] = useState({});
  const [selectedRecord, setSelectedRecord] = useState({});

  // When parent provides an existing record (e.g. loading an edit form), show it
  useEffect(() => {
    if (initialRecord?.uuid) {
      setSelectedRecord(initialRecord);
    }
  }, [initialRecord?.uuid]);

  // Fetch existing items for search
  useEffect(() => {
    if (!query) return setItems([]);
    const fetchItems = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${apiUrl}/${model}?search=${query}`, {
          headers: { 'x-auth-token': token },
        });
        setItems(res.data.data || []);
       
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [query, model]);

  // Fetch metadata for modal
  const fetchMetadata = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${apiUrl}/${model}/metadata`, {
        headers: { 'x-auth-token': token },
      });
      // Filter out UUID, timestamps, etc.
      const fields = res.data.fields.filter(f => !['uuid', 'createdAt', 'updatedAt'].includes(f.name));
      setMetadata(fields);
      // Initialize empty new record
      const initRecord = {};
      fields.forEach(f => { initRecord[f.name] = ''; });
      setNewRecord(initRecord);
    } catch (err) {
      console.error('Metadata fetch error:', err);
    }
  };

  const handleCreateClick = async () => {
    await fetchMetadata();
    setShowModal(true);
  };

  const getItemLabel = (item) => {
    if (!item || typeof item !== 'object') return '';

    // Customers: always show full name + phone (ignores labelField so lists are unambiguous)
    if (model === 'customers') {
      const name = [item.firstname, item.lastname].filter(Boolean).join(' ').trim();
      const parts = [];
      if (name) parts.push(name);
      if (item.phone) parts.push(item.phone);
      if (item.email && !name) parts.push(item.email);
      return parts.join(' · ') || item.uuid || '';
    }

    if (item[labelField]) return item[labelField];

    // Inventory / products priority
    if (item.name && item.sku) return `${item.name} (${item.sku})`;
    if (item.title && item.sku) return `${item.title} (${item.sku})`;

    if (item.name) return item.name;
    if (item.title) return item.title;

    // Generic person-like records (e.g. other models with firstname)
    if (item.firstname || item.lastname) {
      const name = [item.firstname, item.lastname].filter(Boolean).join(' ').trim();
      if (item.phone) return name ? `${name} · ${item.phone}` : item.phone;
      return name || item.firstname || item.lastname || '';
    }

    return item.id || item.uuid || '';
  };


  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setNewRecord(prev => ({ ...prev, [name]: value }));
   
  };

  const handleModalSubmit = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${apiUrl}/${model}/new`, newRecord, {
        headers: { 'x-auth-token': token },
      });
      onChange(res.data.data.uuid);
      setSelectedRecord(res.data.data);
      setItems([]);
      setQuery('');
      setShowModal(false);
    } catch (err) {
      console.error('Create record error:', err);
    }
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        value={selectedRecord?.uuid ? getItemLabel(selectedRecord) : query}
        onChange={(e) => {
          setSelectedRecord(null); // user is typing again, so clear selected
          setQuery(e.target.value);
        }}
        placeholder={placeholder}
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
      />
      
      {items.length > 0 && !selectedRecord && (
        <ul className="absolute z-10 w-full bg-white border shadow max-h-40 overflow-y-auto rounded mt-1">
          {items.map((item) => (
            <li
              key={item.uuid}
              className="p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                const label = getItemLabel(item);
                onChange(item.uuid);
                if (onSelect) onSelect(item);
                setQuery(label);
                setSelectedRecord(item);
                setItems([]);
              }}
            >
              {getItemLabel(item)}
            </li>
          ))}
        </ul>
      )}

      {creatable && query && !loading && !items.some(i => getItemLabel(i).toLowerCase() === query.trim().toLowerCase()) && (
        <button
          type="button"
          onClick={handleCreateClick}
          className="mt-1 px-2 py-1 bg-green-500 text-white rounded"
        >
          Create "{query}"
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">Create New {model}</h3>
            {metadata.map(field => (
              <div key={field.name} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{field.name}</label>
                <input
                  type={field.type === 'INTEGER' || field.type === 'DECIMAL' ? 'number' : 'text'}
                  name={field.name}
                  value={newRecord[field.name]}
                  onChange={handleModalChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  required={!field.allowNull}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}